import { useCallback, useEffect, useRef, useState } from 'react';
import './SignaturePad.css';

/**
 * Signature capture on a canvas.
 *
 * Nothing like this existed in the portal — the performance-agreement signing
 * flow has an engine endpoint but no UI yet — so this is written to be shared
 * rather than specific to investments. It emits a PNG data URL, which is what
 * both /investments/:id/sign and /bookings/:id/sign accept.
 *
 * Pointer events cover mouse, touch and stylus in one path. The canvas is sized
 * to its rendered box times devicePixelRatio so strokes are not blurry on a
 * retina display, and the drawing context is scaled to match so coordinates
 * stay in CSS pixels.
 */
export function SignaturePad({
  onChange, disabled = false, label = 'Sign here',
}: {
  onChange: (dataUrl: string | null) => void;
  disabled?: boolean;
  label?: string;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const dirty = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  const prepare = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const ratio = window.devicePixelRatio || 1;

    canvas.width = Math.round(rect.width * ratio);
    canvas.height = Math.round(rect.height * ratio);

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    // Ink must be dark: the exported PNG is composited onto white in the PDF.
    ctx.strokeStyle = '#0F1420';
  }, []);

  useEffect(() => {
    prepare();
    // Re-preparing on resize clears the canvas, so a mid-signature resize also
    // clears the captured value rather than leaving a stale one attached.
    const onResize = () => { prepare(); dirty.current = false; setHasInk(false); onChange(null); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [prepare, onChange]);

  function pointFrom(e: React.PointerEvent<HTMLCanvasElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function start(e: React.PointerEvent<HTMLCanvasElement>) {
    if (disabled) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drawing.current = true;
    const { x, y } = pointFrom(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  }

  function move(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current || disabled) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = pointFrom(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!dirty.current) { dirty.current = true; setHasInk(true); }
  }

  function end() {
    if (!drawing.current) return;
    drawing.current = false;
    const canvas = canvasRef.current;
    if (!canvas || !dirty.current) return;
    onChange(canvas.toDataURL('image/png'));
  }

  function clear() {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    dirty.current = false;
    setHasInk(false);
    onChange(null);
  }

  return (
    <div className={`sigpad${disabled ? ' sigpad--disabled' : ''}`}>
      <div className="sigpad__frame">
        <canvas
          ref={canvasRef}
          className="sigpad__canvas"
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
          onPointerCancel={end}
          aria-label={label}
          role="img"
        />
        {!hasInk && (
          <span className="sigpad__placeholder" aria-hidden="true">{label}</span>
        )}
        <span className="sigpad__rule" aria-hidden="true" />
      </div>

      <div className="sigpad__actions">
        <span className="sigpad__hint">
          {hasInk ? 'Signature captured.' : 'Draw your signature above.'}
        </span>
        <button
          type="button" className="button button--ghost"
          onClick={clear} disabled={disabled || !hasInk}
        >
          Clear
        </button>
      </div>
    </div>
  );
}
